import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Check } from 'lucide-react';
import { getUserLanguage, setUserLanguage, SUPPORTED_LANGUAGES } from '../../utils/detectLanguage';
import { languageMetadata } from '../../utils/languageConfig';

/**
 * Language Settings Component
 * Allows users to select their preferred language for the platform
 */
const LanguageSettings = ({ t }) => {
  const currentLanguage = getUserLanguage();

  const handleLanguageChange = (langCode) => {
    if (langCode !== currentLanguage) {
      console.log('[LanguageSettings] User selected language:', langCode);
      console.log('[LanguageSettings] Current language before change:', currentLanguage);

      // Set the language preference
      setUserLanguage(langCode);

      // Double-check that localStorage was updated
      const savedLang = localStorage.getItem('userLanguage');
      console.log('[LanguageSettings] Verified localStorage after save:', savedLang);

      if (savedLang !== langCode) {
        console.error('[LanguageSettings] ❌ Failed to save language to localStorage!');
        alert('Failed to save language preference. Please try again.');
        return;
      }

      console.log('[LanguageSettings] ✅ Language saved successfully. Reloading page...');

      // Reload the page to apply new language
      // This ensures all components re-initialize with the new language
      window.location.reload();
    }
  };

  const languages = [
    {
      code: SUPPORTED_LANGUAGES.EN,
      ...languageMetadata[SUPPORTED_LANGUAGES.EN]
    },
    {
      code: SUPPORTED_LANGUAGES.ES,
      ...languageMetadata[SUPPORTED_LANGUAGES.ES]
    },
    {
      code: SUPPORTED_LANGUAGES.PT_BR,
      ...languageMetadata[SUPPORTED_LANGUAGES.PT_BR]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
          <Globe className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white/90">
            {t("Language Preferences")}
          </h2>
          <p className="text-sm text-white/50">
            {t("Choose your preferred language for the platform")}
          </p>
        </div>
      </div>

      {/* Language Options */}
      <div className="space-y-3">
        {languages.map((lang) => (
          <motion.button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-200
              ${currentLanguage === lang.code
                ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-500/20'
                : 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.04] hover:border-white/[0.08]'
              }`}
          >
            <div className="flex items-center gap-4">
              <div className="text-3xl">{lang.flag}</div>
              <div className="text-left">
                <div className="text-sm font-medium text-white/90">
                  {lang.nativeName}
                </div>
                <div className="text-xs text-white/50">
                  {lang.name}
                </div>
              </div>
            </div>

            {currentLanguage === lang.code && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-2"
              >
                <span className="text-xs text-emerald-400/90 font-medium">
                  {t("Active")}
                </span>
                <div className="p-1 bg-emerald-500/20 rounded-full">
                  <Check className="w-3 h-3 text-emerald-400" />
                </div>
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-blue-500/10 rounded-md mt-0.5">
            <Globe className="w-4 h-4 text-blue-400/80" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-white/70 leading-relaxed">
              {t("The platform will automatically detect your browser language. You can override this by selecting a language above.")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSettings;
